import { Opportunity as CatalogOpportunity, OpportunityStatus } from '../../pages/CampaignCatalog/types';
import { formatDate } from '../../utils/dateFormat';
import { expandBookingSlotsToDays } from '../../utils/scheduleDays';
import { formatDateRangeWithGmt, TIMEZONE_MAP } from '../../utils/timezone';
import {
  ApiCampaignListItem,
  ApiDealAssetStatus,
  ApiIoValidation,
  ApiOpportunityStatus,
  DealAssetStatus,
  RateType,
} from './types';

// ─── File-key bucket inference ───────────────────────────────

import { BucketType, bucketForKey } from './bucket';

/** Returns true when the S3 key resolves to the external (CDN-served, merchant-visible) bucket. */
export const isMoopExternalKey = (key: string): boolean => bucketForKey(key) === BucketType.EXTERNAL;

// ─── Status mapping: API → Frontend ──────────────────────────

/** Map backend opportunity status to frontend display status */
export function mapOpportunityStatus(apiStatus: ApiOpportunityStatus): OpportunityStatus {
  switch (apiStatus) {
    case 'PENDING_BD':
    case 'PENDING_CHECK':
      return 'PENDING_SB_REVIEW';
    case 'PENDING_CONTROLLER':
      return 'PENDING_SB_REVIEW'; // TW-only internal, show as "pending review"
    default:
      if (process.env.NODE_ENV === 'development') {
        console.warn(`[CampaignManager] Unknown status mapping: ${apiStatus}`);
      }
      return apiStatus as OpportunityStatus;
  }
}

/** Map backend deal asset status to frontend display status */
export function mapDealAssetStatus(apiStatus: ApiDealAssetStatus): OpportunityStatus {
  switch (apiStatus) {
    case 'PENDING_MKT':
    case 'PENDING_CRM':
    case 'PENDING_GD':
    case 'PENDING_ML':
      return 'PENDING_SB_REVIEW'; // All internal deal statuses → "Pending ShopBack review"
    default:
      if (process.env.NODE_ENV === 'development') {
        console.warn(`[CampaignManager] Unknown status mapping: ${apiStatus}`);
      }
      return apiStatus as OpportunityStatus;
  }
}

// ─── Status mapping: Frontend → API ──────────────────────────

/** Map frontend status to backend opportunity status for PATCH */
export function toApiOpportunityStatus(feStatus: OpportunityStatus): ApiOpportunityStatus {
  switch (feStatus) {
    case 'PENDING_SB_REVIEW':
      return 'PENDING_BD';
    default:
      if (process.env.NODE_ENV === 'development') {
        console.warn(`[CampaignManager] Unknown status mapping: ${feStatus}`);
      }
      return feStatus as ApiOpportunityStatus;
  }
}

/** Expand a merchant-facing filter bucket to every internal opportunity status it represents. */
export function toApiOpportunityFilterStatuses(feStatus: OpportunityStatus): ApiOpportunityStatus[] {
  if (feStatus === 'PENDING_SB_REVIEW') {
    return ['PENDING_BD', 'PENDING_CHECK', 'PENDING_CONTROLLER'];
  }
  return [toApiOpportunityStatus(feStatus)];
}

/**
 * Map frontend status to backend deal asset status for PATCH.
 *
 * WARNING: This is a lossy mapping — PENDING_SB_REVIEW always maps to PENDING_MKT.
 * Use only for creating new items or when the caller knows the current backend status.
 * For status-change PATCHes on existing items, prefer storing the original API status.
 */
export function toApiDealAssetStatus(feStatus: OpportunityStatus): ApiDealAssetStatus {
  switch (feStatus) {
    case 'PENDING_SB_REVIEW':
      return DealAssetStatus.PENDING_MKT;
    default:
      if (process.env.NODE_ENV === 'development') {
        console.warn(`[CampaignManager] Unknown status mapping: ${feStatus}`);
      }
      return feStatus as ApiDealAssetStatus;
  }
}

// ─── Type mapping ────────────────────────────────────────────

type FrontendType = 'UPSIZE' | 'AD_SALES' | 'STANDALONE_OFFER';

export function mapApiTypeToFrontend(apiType: 'AD_SALES' | 'UPSIZE' | 'DEAL'): FrontendType {
  return apiType === 'DEAL' ? 'STANDALONE_OFFER' : apiType;
}

export function mapFrontendTypeToApi(feType: FrontendType): 'AD_SALES' | 'UPSIZE' | 'DEAL' {
  return feType === 'STANDALONE_OFFER' ? 'DEAL' : feType;
}

// ─── Row mapping: API → Frontend CatalogOpportunity ──────────

// The API sends UTC instants. Rendering them with local Date getters re-based every
// date into the viewer's browser zone, so an end date of 23:59:59Z showed as the
// next day for every market east of UTC. Always resolve against the market.
const DATE_TIME_FMT = 'DD MMM YYYY HH:mm';

/** Single date in market time, for timestamps that carry no range. */
function fmtMarketDate(iso: string | null, country?: string): string {
  if (!iso) return '—';
  return formatDate(iso, 'DD MMM YYYY', country ? TIMEZONE_MAP[country.toUpperCase()] : undefined);
}

/** Date range in market time with its GMT offset appended. */
function fmtDateRange(start: string | null, end: string | null, country?: string): string {
  return formatDateRangeWithGmt(start, end, DATE_TIME_FMT, country);
}

/** Map a single API list item to a frontend CatalogOpportunity */
export function mapListItemToCatalogOpportunity(item: ApiCampaignListItem): CatalogOpportunity {
  const base = {
    id: item.id,
    numericId: item.id, // API id used directly; display formatting is client-side
    brand: item.merchant.name,
    merchantId: item.merchant.id,
    country: item.merchant.country,
    startDate: item.startDate,
    endDate: item.endDate,
    startTime: null,
    endTime: null,
    timezone: TIMEZONE_MAP[(item.country || '').toUpperCase()] ?? '',
    dates: fmtDateRange(item.startDate, item.endDate, item.country),
    creationSource: item.creationSource,
    createdBy: item.createdBy,
    submittedAt: item.createdAt ? fmtMarketDate(item.createdAt, item.country) : null,
    // An Upsize's exposure package; the table falls back to this when a row carries no nested
    // packages, which is every Upsize row (MIC-3847).
    subtype: item.packageName ?? null,
  };

  // Normalize status to uppercase before mapping — API may return mixed case
  const normalizedStatus = (item.status || '').toUpperCase();

  switch (item.type) {
    case 'AD_SALES':
      return {
        ...base,
        type: 'AD_SALES',
        status: mapOpportunityStatus(normalizedStatus as ApiOpportunityStatus),
        apiStatus: normalizedStatus,
        ...(item.ioValidation?.id && { ioValidationId: item.ioValidation.id }),
        ...(item.ioValidation?.status && { ioValidationStatus: item.ioValidation.status }),
        // Surface the nested packages so the catalog can render
        // multi-package campaigns as grouped rows. Each package keeps
        // its own status + dates so the per-package StatusTag can be
        // derived independently from the campaign-level status.
        packages: (item.packages ?? []).map((p) => ({
          id: p.id,
          // BE sends `type`; fall back to `packageType` for any legacy/mock payloads.
          packageType: p.type ?? p.packageType,
          name: p.name,
          status: mapOpportunityStatus((p.status || '').toUpperCase() as ApiOpportunityStatus),
          startDate: p.startDate ?? null,
          endDate: p.endDate ?? null,
          dates: fmtDateRange(p.startDate ?? null, p.endDate ?? null, item.country),
          ...(typeof p.assetCount === 'number' && { assetCount: p.assetCount }),
        })),
      };
    case 'UPSIZE':
      return {
        ...base,
        type: 'UPSIZE',
        status: mapOpportunityStatus(normalizedStatus as ApiOpportunityStatus),
      };
    case 'DEAL':
      return {
        ...base,
        type: 'STANDALONE_OFFER',
        status: mapDealAssetStatus(normalizedStatus as ApiDealAssetStatus),
      };
    default: {
      if (process.env.NODE_ENV === 'development') {
        console.warn(`[CampaignManager] Unknown item type: ${(item as Record<string, unknown>).type}`);
      }
      return {
        ...base,
        type: 'UPSIZE' as const,
        status: 'DRAFT' as OpportunityStatus,
      };
    }
  }
}

/** Map an array of API list items to frontend opportunities */
export function mapListItemsToCatalogOpportunities(items: ApiCampaignListItem[]): CatalogOpportunity[] {
  return items.map(mapListItemToCatalogOpportunity);
}

// ─── Detail response mapping: API → DetailOpportunity ────────

import { Opportunity as DetailOpportunity } from '../../pages/CampaignDetail/opportunity';

function mapDetailOpportunityStatus(apiType: 'AD_SALES' | 'UPSIZE' | 'DEAL', rawStatus: string): OpportunityStatus {
  if (apiType === 'DEAL') {
    return mapDealAssetStatus(rawStatus as ApiDealAssetStatus);
  }
  return mapOpportunityStatus(rawStatus as ApiOpportunityStatus);
}

/**
 * Map a V2 API detail response (from GET /opportunities/:id, GET /opportunity-groups/:id,
 * or GET /deal-assets/:id) to the frontend DetailOpportunity shape.
 *
 * The API response shapes differ by type; this mapper normalizes them into
 * the common Opportunity interface used by CampaignDetail page components.
 */
export function mapApiDetailToOpportunity(
  apiType: 'AD_SALES' | 'UPSIZE' | 'DEAL',
  raw: Record<string, unknown>
): DetailOpportunity {
  const rawStatus = String(raw.status || raw.assetStatus || 'DRAFT');
  const ioValidation = raw.ioValidation as ApiIoValidation | null | undefined;
  const base: Partial<DetailOpportunity> = {
    id: String(raw.id ?? ''),
    status: mapDetailOpportunityStatus(apiType, rawStatus),
    ...(apiType !== 'DEAL' && { apiStatus: rawStatus as ApiOpportunityStatus }),
    ...(ioValidation && { ioValidation }),
    brand: String((raw.merchant as Record<string, unknown>)?.name || raw.merchantName || ''),
    merchantId: String((raw.merchant as Record<string, unknown>)?.id || raw.merchantId || ''),
    country: String(raw.country || ''),
    createdBy: String(raw.createdBy || raw.requester || ''),
    ...(raw.creationSource && { creationSource: String(raw.creationSource) }),
    ...(raw.operatorComment && { operatorComment: String(raw.operatorComment) }),
    // MIC-3870: the BD's reason for pitching this booking, shown on the approve/reject card.
    ...(raw.pitchNote && { pitchNote: String(raw.pitchNote) }),
    submittedAt: raw.createdAt ? fmtMarketDate(String(raw.createdAt), String(raw.country || '')) : undefined,
  };

  if (apiType === 'AD_SALES') {
    // After Fix 4 the GET response uses `packages[]` (not `subOpportunities[]`),
    // each with a flat `assets?: AssetBookingAssetDto[]` (not nested in assetBookingOpportunity).
    const pkgs = (raw.packages || []) as Array<Record<string, unknown>>;
    const startDates = pkgs.map((p) => p.startDate).filter(Boolean) as string[];
    const endDates = pkgs.map((p) => p.endDate).filter(Boolean) as string[];

    // Hydrate assetSchedule for each package from bookingSlots (for edit/duplicate).
    // Slot bounds are stored at market midnight, so they must be read back in the market's
    // zone — decoding in UTC puts every picked day one day early for east-of-UTC markets.
    const marketZone = TIMEZONE_MAP[String(raw.country || '').toUpperCase()];
    const hydratedPkgs = pkgs.map((pkg) => {
      const assets = (pkg.assets ?? []) as Array<Record<string, unknown>>;
      const assetSchedule: Record<string, Date[]> = {};
      for (const asset of assets) {
        const slots = (asset.bookingSlots ?? []) as Array<{ startTime: string; endTime: string }>;
        if (slots.length > 0 && asset.name) {
          assetSchedule[String(asset.name)] = expandBookingSlotsToDays(slots, marketZone);
        }
      }
      return { ...pkg, assetSchedule };
    });

    const payment = raw.payment as Record<string, unknown> | null | undefined;
    const iu = (payment?.invoicingUnit ?? {}) as Record<string, unknown>;
    const summary = payment?.summary as Record<string, string> | null | undefined;

    // Reverse-map legalEntity from invoicingUnit extended fields (MIC-2898).
    // companyName doubles as account name for legacy records that predate the extension.
    const legalEntity: Record<string, unknown> = {
      accountName: iu.companyName,
      country: iu.country,
      companyRegNumber: iu.companyRegistrationNumber,
      taxNumber: iu.taxRegistrationNumber,
      invoicingAddress: iu.invoicingAddress,
      salesforceId: iu.salesforceId,
    };

    // Reverse-map bankAccount. email is the primary contact for legacy records.
    const bankAccount: Record<string, unknown> = {
      name: iu.accountName,
      holderName: iu.accountHolderName,
      contactEmail: iu.email,
      billingCurrency: iu.billingCurrency,
      billingAddress: iu.billingAddress,
      mailingAddress: iu.mailingAddress,
      billingSameAsInvoicing: iu.billingSameAsInvoicing ?? true,
      mailingSameAsBilling: iu.mailingSameAsBilling ?? true,
    };

    // Authoritative price totals — prefer payment.summary; fall back to nothing
    // (AdSalesPriceSummaryCard has a catalog-based fallback for SEED data).
    const priceSummary = summary
      ? {
          currency: String(summary.currency || 'SGD'),
          totalPrice: Number(summary.totalPrice || 0),
          totalGstVat: Number(summary.totalGstVat || 0),
          grandTotal: Number(summary.grandTotal || 0),
        }
      : undefined;

    // signedIO — map ioAttachments of type 'signed' (or any attachment for legacy).
    const ioAttachments = (payment?.ioAttachments ?? []) as Array<Record<string, string>>;
    const signedIO = ioAttachments
      .filter((a) => !a.type || a.type === 'signed')
      .map((a) => ({ name: a.name, url: a.fileKey }));

    // generatedIO — ShopBack-generated IO PDF link set by the backend at IO generation time.
    const generatedIOLink = payment?.generatedInvoiceLink ? String(payment.generatedInvoiceLink) : undefined;
    const generatedIO = generatedIOLink
      ? {
          name: generatedIOLink.split('/').pop()?.split('?')[0] ?? 'Insertion_Order.pdf',
          url: generatedIOLink,
        }
      : undefined;

    // Group-level merchantComment (decision comment from approve/reject/send-back) wins;
    // fall back to the first package that carries a creation note.
    const merchantComment = String(
      raw.merchantComment || pkgs.map((p) => (p as Record<string, unknown>)?.merchantComment).find(Boolean) || ''
    );

    return {
      ...base,
      type: 'AD_SALES',
      description: `Ad Sales · ${pkgs.length} package${pkgs.length !== 1 ? 's' : ''}`,
      dates: fmtDateRange(
        startDates.length ? startDates.sort()[0] : null,
        endDates.length ? endDates.sort().reverse()[0] : null,
        String(raw.country || '')
      ),
      startDate: startDates.length ? new Date(startDates.sort()[0]) : null,
      endDate: endDates.length ? new Date(endDates.sort().reverse()[0]) : null,
      selected: hydratedPkgs,
      // billingInfo is "Direct" | "Agency" on the BE; map back to wizard's "Direct" | "Network"
      billingType: payment?.billingInfo === 'Agency' ? 'Network' : 'Direct',
      legalEntity,
      bankAccount,
      poNumber: payment?.insertionOrderReferenceId ? String(payment.insertionOrderReferenceId) : undefined,
      ...(priceSummary && { priceSummary }),
      ...(generatedIO && { generatedIO }),
      ...(signedIO.length > 0 && { signedIO }),
      merchantComment,
      _rawPayment: raw.payment,
    } as DetailOpportunity;
  }

  if (apiType === 'UPSIZE') {
    const ratsRate = raw.ratsRate as { tiers?: Array<Record<string, unknown>> } | null | undefined;
    const baselineRatsRate = raw._baselineRatsRate as { tiers?: Array<Record<string, unknown>> } | null | undefined;
    const commissionTiers = (ratsRate?.tiers ?? [])
      .filter((t) => t.enabled !== false)
      .map((t) => {
        const baselineTier = baselineRatsRate?.tiers?.find((bt) => bt.tierLabel === t.tierLabel);
        return {
          category: String(t.marketingLabel || t.tierLabel || ''),
          commissionType: (t.commissionType as string) === RateType.FIXED ? RateType.FIXED : RateType.PERCENTAGE,
          commissionRate: Number(t.commissionRate ?? 0),
          cashbackType: (t.cashbackType as string) === RateType.FIXED ? RateType.FIXED : RateType.PERCENTAGE,
          cashbackRate: Number(t.cashbackRate ?? 0),
          commissionCap: t.commissionCap !== null && t.commissionCap !== undefined ? Number(t.commissionCap) : null,
          cashbackCap: t.cashbackCap !== null && t.cashbackCap !== undefined ? Number(t.cashbackCap) : null,
          enabled: t.enabled !== false,
          ...(baselineTier !== undefined && {
            baselineCommissionRate: Number(baselineTier.commissionRate ?? 0),
            baselineCommissionType:
              (baselineTier.commissionType as string) === RateType.FIXED ? RateType.FIXED : RateType.PERCENTAGE,
            baselineCashbackRate: Number(baselineTier.cashbackRate ?? 0),
            baselineCashbackType:
              (baselineTier.cashbackType as string) === RateType.FIXED ? RateType.FIXED : RateType.PERCENTAGE,
          }),
        };
      });
    const upsizeData = raw.upsize as Record<string, unknown> | null | undefined;
    const networkCheckScreenshots = (upsizeData?.networkCheckScreenshots ?? []) as string[];
    // Only merchant-uploaded (external-bucket) proof is renderable from MOOP — ops keys
    // live in the internal bucket, which merchant surfaces must not resolve.
    const networkProof = networkCheckScreenshots.filter(isMoopExternalKey).map((key) => ({
      uid: key,
      name: key.split('/').pop() || key,
      status: 'UPLOADED' as const,
    }));
    return {
      ...base,
      type: 'UPSIZE',
      description: `Upsize · ${raw.packageTier || 'No package'}`,
      dates: fmtDateRange(raw.startDate as string | null, raw.endDate as string | null, String(raw.country || '')),
      startDate: raw.startDate ? new Date(raw.startDate as string) : null,
      endDate: raw.endDate ? new Date(raw.endDate as string) : null,
      merchantComment: String(raw.merchantComment || ''),
      ...(raw.packageTier && { packageTier: String(raw.packageTier) }),
      ...(commissionTiers.length > 0 && { commissionTiers }),
      ...(raw.campaignId && { campaignId: String(raw.campaignId) }),
      ...((raw.campaignName || (raw.campaign as Record<string, unknown>)?.name) && {
        campaignName: String(raw.campaignName || (raw.campaign as Record<string, unknown>)?.name || ''),
      }),
      ...(networkProof.length > 0 && { networkProof }),
      // Raw column value (all buckets) — carried so proof submits can preserve
      // ops-uploaded internal keys that the merchant view filters out.
      ...(networkCheckScreenshots.length > 0 && { networkCheckScreenshots }),
      ...(typeof raw.networkCheckType === 'string' && { networkCheckType: raw.networkCheckType }),
      ...(Array.isArray(raw.dealAssets) && { dealAssets: raw.dealAssets }),
      ...(raw.affiliateLink && { affiliateLink: String(raw.affiliateLink) }),
      ...(typeof raw.isMerchantInSupportNetwork === 'boolean' && {
        isMerchantInSupportNetwork: raw.isMerchantInSupportNetwork,
      }),
    } as DetailOpportunity;
  }

  // DEAL → STANDALONE_OFFER
  const contents = ((raw.contents || []) as Array<Record<string, unknown>>)[0] || {};
  // `opportunityIds` (plural) is the authoritative source — computed live from the join table.
  // `opportunityId` (singular) is a deprecated legacy column that link/unlink never updates; do
  // NOT fall back to it (a stale value there is exactly what caused the "still shows linked" bug
  // after unlinking all opportunities).
  const linkedOpportunityIds = Array.isArray(raw.opportunityIds)
    ? (raw.opportunityIds as Array<string | number>).map(String)
    : [];
  return {
    ...base,
    type: 'STANDALONE_OFFER',
    description: String(contents.title || raw.assetName || ''),
    dates: fmtDateRange(raw.startTime as string | null, raw.endTime as string | null, String(raw.country || '')),
    startDate: raw.startTime ? new Date(raw.startTime as string) : null,
    endDate: raw.endTime ? new Date(raw.endTime as string) : null,
    title: String(contents.title || raw.assetName || ''),
    descriptionLong: String(contents.description || ''),
    landingUrl: String(contents.landingPageUrl || raw.affiliateLink || ''),
    image: String(contents.imageUrl || ''),
    linkedOpportunityIds,
    // Bridge for the single-select create/edit form prefill and clone flow.
    // Sourced from the authoritative plural array — never from the deprecated singular.
    linkedOpportunityId: linkedOpportunityIds[0],
  } as DetailOpportunity;
}

// ─── PATCH routing: type → endpoint path ─────────────────────

/** Get the correct PATCH endpoint path for a given campaign type */
export function getPatchPath(type: FrontendType, id: string): string {
  switch (type) {
    case 'AD_SALES':
      return `/opportunity-groups/${id}`;
    case 'UPSIZE':
      return `/opportunities/${id}`;
    case 'STANDALONE_OFFER':
      return `/deal-assets/${id}`;
  }
}
